import { zodResolver } from '@hookform/resolvers/zod';
import { Stack } from 'expo-router';
import ConfirmPasswordLabels from 'features/myVaro/screens/PersonalDetails/ConfirmPassword/ConfirmPasswordLabels';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Keyboard,
  StyleSheet,
  TouchableWithoutFeedback,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Button from 'shared/components/vxl/Button/Button';
import Dialog from 'shared/components/vxl/Dialog/Dialog';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import { passwordSchema } from 'shared/components/vxl/input/schemas/password.schema';
import VXLTextInput from 'shared/components/vxl/input/VXLTextInput/VXLTextInput';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import { NavigationBar } from 'shared/components/vxl/navigation/NavigationBar/NavigationBar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import { isWeb } from 'shared/constants/platform';
import DefaultPageDownErrorMessage from 'shared/patterns/vxl/errors/PageDownErrorMessage/DefaultPageDownErrorMessage';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';
import { z } from 'zod';

type PasswordScreenProps = {
  title: string;
  header: string;
  newPasswordLabel: string;
  confirmPasswordLabel: string;
  confirmPasswordErrorLabel: string;
  submitButtonLabel: string;
  onSubmit: (password: string) => Promise<void>;
  loading?: boolean;
  dialogContent?: { title?: string; message?: string };
  onDialogClose?: () => void;
  onBack?: () => void;
  tryAgainAction?: () => void;
  showErrorScreen?: boolean;
};

const SetPassword = ({
  title,
  header,
  newPasswordLabel,
  confirmPasswordLabel,
  confirmPasswordErrorLabel,
  submitButtonLabel,
  onSubmit,
  loading = false,
  dialogContent,
  onDialogClose,
  onBack,
  tryAgainAction,
  showErrorScreen = false,
}: PasswordScreenProps) => {
  const { t } = useTranslation();
  const insets = useSafeAreaInsets();

  const [isNewPasswordVisible, setNewPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const { control } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: { password: '' },
    resolver: zodResolver(
      z.object({
        password: passwordSchema,
      }),
    ),
  });

  const isNewPasswordValid = passwordSchema.safeParse(newPassword).success;
  const passwordsMatch = newPassword === confirmPassword;
  const confirmPasswordError = confirmPassword.length > 0 && !passwordsMatch;
  const isSubmitEnabled =
    confirmPassword.length > 0 && passwordsMatch && isNewPasswordValid;

  const handleSubmit = async () => {
    if (isSubmitEnabled) {
      await onSubmit(newPassword);
    }
  };

  const isErrorShown = showErrorScreen && !!dialogContent?.message;

  return (
    <TouchableWithoutFeedback
      accessible={false}
      onPress={() => {
        if (!isWeb) {
          Keyboard.dismiss();
        }
      }}
      testID="set-password-touchable"
    >
      <View style={styles.rootContainer}>
        <ScreenTransitionOverlay
          LoadingComponent={<LoadingSpinner />}
          isLoading={loading}
          ErrorComponent={
            isErrorShown ? (
              <DefaultPageDownErrorMessage
                error={isErrorShown}
                title={dialogContent?.title}
                message={dialogContent?.message}
                tryAgainAction={tryAgainAction ?? (() => {})}
              />
            ) : null
          }
        />

        <Stack.Screen
          options={{
            title,
            headerShown: true,
            headerBackVisible: true,
            header: () => (
              <NavigationBar
                title={title}
                primaryIcon={<Icon iconKey="BACK" iconSize="MD" />}
                primaryOnPress={onBack}
                transparent={false}
              />
            ),
          }}
        />

        <View
          style={[
            styles.container,
            { paddingBottom: insets.bottom || theme.spacing.m },
          ]}
        >
          <View style={styles.parent}>
            <View style={styles.baseContainer}>
              <TitleLgText numberOfLines={0}>{header}</TitleLgText>

              <View style={styles.textFieldContainer}>
                <Controller
                  control={control}
                  name="password"
                  render={({ field: { onChange, onBlur } }) => (
                    <View style={styles.input}>
                      <VXLTextInput
                        testID={`text-input-${newPasswordLabel}`}
                        labelText={newPasswordLabel}
                        onChangeText={(text) => {
                          setNewPassword(text);
                          onChange(text);
                          onBlur();
                        }}
                        value={newPassword}
                        onBlur={onBlur}
                        secureTextEntry={!isNewPasswordVisible}
                        autoCapitalize="none"
                        inputMode="text"
                        rightIcon={{
                          iconKey: !isNewPasswordVisible ? 'VIEW' : 'HIDE',
                          iconAccessibilityLabel: isNewPasswordVisible
                            ? 'eye-close'
                            : 'eye-open',
                          onPress: () =>
                            setNewPasswordVisible(
                              (currentValue) => !currentValue,
                            ),
                        }}
                        onSubmitEditing={handleSubmit}
                      />
                    </View>
                  )}
                />

                <ConfirmPasswordLabels newPassword={newPassword} />

                <View style={styles.input}>
                  <VXLTextInput
                    testID={`text-input-${confirmPasswordLabel}`}
                    labelText={confirmPasswordLabel}
                    onChangeText={(text) => setConfirmPassword(text)}
                    value={confirmPassword}
                    errorText={
                      confirmPasswordError
                        ? confirmPasswordErrorLabel
                        : undefined
                    }
                    analyticsId="confirmPasswordFieldClicked"
                    secureTextEntry={!isConfirmPasswordVisible}
                    autoCapitalize="none"
                    inputMode="text"
                    rightIcon={{
                      iconKey: !isConfirmPasswordVisible ? 'VIEW' : 'HIDE',
                      iconAccessibilityLabel: isConfirmPasswordVisible
                        ? 'eye-close'
                        : 'eye-open',
                      onPress: () =>
                        setConfirmPasswordVisible(
                          (currentValue) => !currentValue,
                        ),
                    }}
                    onSubmitEditing={handleSubmit}
                  />
                </View>
              </View>
            </View>

            <Button
              isLoading={loading}
              isDisabled={!isSubmitEnabled}
              onPress={handleSubmit}
              label={submitButtonLabel}
            />
          </View>
        </View>

        <Dialog
          isDialogVisible={
            !showErrorScreen &&
            !!(dialogContent?.title || dialogContent?.message)
          }
          titleText={
            dialogContent?.title ||
            t('auth:resetPassword.createPassword.texts.error')
          }
          messageText={
            <BodyBaseText testID="set-password-dialog">
              {dialogContent?.message ?? ''}
            </BodyBaseText>
          }
          dialogButtonProps={[
            {
              label: t('auth:resetPassword.createPassword.buttons.ok'),
              onClick: onDialogClose,
            },
          ]}
        />
      </View>
    </TouchableWithoutFeedback>
  );
};

const styles = StyleSheet.create({
  baseContainer: { gap: theme.spacing.m },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    padding: theme.spacing.m,
  },
  input: { width: '100%' },
  parent: { flexDirection: 'column', flex: 1, justifyContent: 'space-between' },
  rootContainer: { flex: 1 },
  textFieldContainer: { gap: theme.spacing.m, width: '100%' },
});

export default SetPassword;
